import { useCallback, useEffect, useState } from 'react';
import { useAdminApiV1 } from '../../../../clients/quipli-service/admin/admin-api-v1';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { Location } from '../../../../clients/quipli-service/location/location';
import {
  isActionable,
  isQuote,
  isRequest,
  OrderCardProcessedDetails,
  OrderDirection,
  orderToOrderCardPropsMapper,
} from '../dashboard-utils';
import { Order, OrderDocumentType, OrderReturnStatus } from '../../../../clients/quipli-service/order/order';
import { Admin } from '../../../../clients/quipli-service/admin/admin';
import { useReportApiV2 } from '../../../../clients/quipli-service/report/report-api-v2';
import { ProductUnitsReportSummary } from '../../../../clients/quipli-service/report/report';
import { DateTime } from 'luxon';
import { mergeOrder } from '../../../report/revenue/revenue-orders-table';
import { debounce, Dictionary, groupBy } from 'lodash';
import { useJobTaskApi } from '../../../../clients/quipli-service/job-tasks/job-task-api';
import { Job } from '../../../../clients/quipli-service/job-tasks/job-tasks';

export type DashboardKPIData = ProductUnitsReportSummary & {
  'Order Requests': number;
};

export const useDashboardData = (props: {
  from: DateTime | null;
  to: DateTime | null;
  searchText: string;
  hideActioned: boolean;
  hideQuotes: boolean;
  hideRequests: boolean;
}) => {
  const { from, to, searchText, hideActioned, hideQuotes, hideRequests } = props;
  const { productUnitsSummary } = useReportApiV2();
  const { listLocations, getMultipleConfigurations, searchOrders } = useAPI();
  const { getAdminsList } = useAdminApiV1();
  const { getJobsByMultipleEntityIds } = useJobTaskApi();

  // Network
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingError, setLoadingError] = useState<boolean>(false);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [summaryError, setSummaryError] = useState<boolean>(false);
  const [unitTrackingViaUnitNum, setUnitTrackingViaUnitNum] = useState<boolean>();
  const [inspectionEnabled, setInspectionEnabled] = useState<boolean>();

  // Orders
  const [ordersStartingToday, setOrdersStartingToday] = useState<OrderCardProcessedDetails[]>([]);
  const [ordersEndingToday, setOrdersEndingToday] = useState<OrderCardProcessedDetails[]>([]);
  const [combinedOrders, setCombinedOrders] = useState<OrderCardProcessedDetails[]>([]);
  const [pastDueOrders, setPastDueOrders] = useState<OrderCardProcessedDetails[]>([]);
  const [originalOrders, setOriginalOrders] = useState<Order[]>([]);
  const [drivers, setDrivers] = useState<Admin[]>([]);
  const [summaryData, setSummaryData] = useState<DashboardKPIData>({
    'Current Utilization %': 0,
    Total: 0,
    'On Rent': 0,
    Available: 0,
    'Hard Down': 0,
    'Soft Down': 0,
    'Past Due': 0,
    'Order Requests': 0,
  });

  // Location
  const [allLocations, setAllLocations] = useState<Location[] | null>();
  const [location, setLocation] = useState<Location | null | undefined>(undefined);

  const getSummary = useCallback(
    debounce(async () => {
      if (location === undefined) {
        return;
      }
      try {
        setSummaryLoading(true);
        const configs = await getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_INSPECTIONS_ENABLED']);
        setUnitTrackingViaUnitNum(
          configs.find((config) => config.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '0',
        );
        setInspectionEnabled(configs.find((config) => config.variable === 'CONF_INSPECTIONS_ENABLED')?.value === '1');

        const summary = await productUnitsSummary({
          locationIds: location ? [location.id] : undefined,
          pageNumber: 1,
          pageSize: 10000,
        });

        const requestedOrders = await searchOrders({
          sortBy: 'rentalStart',
          sortDirection: 'desc',
          pageNumber: 1,
          pageSize: 10000,
          keyword: searchText,
          locationId: location?.id ?? undefined,
          includeOverdue: true,
          workFlowStatus: ['Requests'],
          documentType: OrderDocumentType.Order,
        });

        setSummaryData({ ...summary, 'Order Requests': requestedOrders.items.length });
      } catch (e) {
        console.error(e);
        setSummaryError(true);
      } finally {
        setSummaryLoading(false);
      }
    }, 500),
    [
      location,
      searchText,
      productUnitsSummary,
      getMultipleConfigurations,
      searchOrders,
      setSummaryData,
      setUnitTrackingViaUnitNum,
      setSummaryLoading,
      setSummaryError,
    ],
  );

  useEffect(() => {
    listLocations().then((locations) => {
      setAllLocations(locations);
    });
  }, []);

  useEffect(() => {
    getSummary();
  }, [location]);

  const findLocation = (order: Order, allLocations: Location[]): Location | undefined =>
    allLocations.find((l) => l.id === order?.locationId);

  const mapOrders = (orders: Order[], allLocations: Location[], direction: OrderDirection[], jobs: Dictionary<Job[]>) =>
    orders.map((order) =>
      orderToOrderCardPropsMapper(
        order,
        DateTime.local(),
        findLocation(order, allLocations),
        direction,
        jobs[order.id],
      ),
    );

  const getOrders = async () => {
    if (!loading && location !== undefined && allLocations && (from || to)) {
      try {
        setLoading(true);
        const [startingToday, endingToday, pastDue30Days, adminList] = await Promise.all([
          searchOrders({
            startDateFrom: from ?? undefined,
            startDateTo: to ?? undefined,
            sortBy: 'rentalStart',
            sortDirection: 'desc',
            pageNumber: 1,
            pageSize: 10000,
            keyword: searchText,
            locationId: location?.id ?? undefined,
            includeOverdue: true,
            workFlowStatus: ['Approved', 'Off Rent', 'On Rent', 'Completed', 'Quote', 'Requests'],
            documentType: OrderDocumentType.Order,
          }),
          searchOrders({
            endDateFrom: from ?? undefined,
            endDateTo: to ?? undefined,
            sortBy: 'rentalEnd',
            sortDirection: 'desc',
            pageNumber: 1,
            pageSize: 10000,
            keyword: searchText,
            locationId: location?.id ?? undefined,
            includeOverdue: true,
            workFlowStatus: ['Approved', 'Off Rent', 'On Rent', 'Completed', 'Quote', 'Requests'],
            documentType: OrderDocumentType.Order,
          }),
          searchOrders({
            endDateTo: DateTime.local(),
            sortBy: 'rentalStart',
            sortDirection: 'desc',
            pageNumber: 1,
            pageSize: 10000,
            keyword: searchText,
            locationId: location?.id ?? undefined,
            includeOverdue: true,
            workFlowStatus: ['On Rent'],
            documentType: OrderDocumentType.Order,
          }),
          getAdminsList(),
        ]);

        setDrivers(adminList);

        const activeStartingToday = startingToday.items.filter((order) => order.status !== 'Cancelled');
        const activeEndingToday = endingToday.items.filter((order) => order.status !== 'Cancelled');

        const orders = mergeOrder(activeStartingToday, activeEndingToday, 'id', 'desc');

        setOriginalOrders(orders);

        const jobs =
          inspectionEnabled && orders.length > 0
            ? await getJobsByMultipleEntityIds(
                'Order',
                orders.map((order) => `${order.id}`),
              ).then((jobs) => groupBy(jobs, 'entityId'))
            : {};

        const findLocation = (order: Order): Location | undefined =>
          allLocations.find((l) => l.id === order?.locationId);

        setOrdersStartingToday(mapOrders(activeStartingToday, allLocations, [OrderDirection.GoingOut], jobs));
        setOrdersEndingToday(mapOrders(activeEndingToday, allLocations, [OrderDirection.ComingIn], jobs));

        // 'Duped' items will appear in both lists, want these so we can mark their direction as both
        const directions: (OrderDirection.ComingIn | OrderDirection.GoingOut)[][] = orders.map((order) => {
          const isGoingOut = activeStartingToday.some((o) => o.id === order.id);
          const isComingIn = activeEndingToday.some((o) => o.id === order.id);
          return isGoingOut && isComingIn
            ? [OrderDirection.ComingIn, OrderDirection.GoingOut]
            : isGoingOut
            ? [OrderDirection.GoingOut]
            : [OrderDirection.ComingIn];
        });

        const combinedOrdersMapped = orders.map((order, i) =>
          orderToOrderCardPropsMapper(order, DateTime.local(), findLocation(order), directions[i], jobs[order.id]),
        );
        setCombinedOrders(combinedOrdersMapped);

        const pastDueDirections: (OrderDirection.ComingIn | OrderDirection.GoingOut)[][] = pastDue30Days.items.map(
          (order) => {
            const isGoingOut = order.returnStatus === OrderReturnStatus.NotStarted;
            const isComingIn = order.returnStatus !== OrderReturnStatus.Complete;
            return isGoingOut && isComingIn
              ? [OrderDirection.ComingIn, OrderDirection.GoingOut]
              : isGoingOut
              ? [OrderDirection.GoingOut]
              : [OrderDirection.ComingIn];
          },
        );

        const pastDueOrdersMapped = pastDue30Days.items
          .filter((i) => i.status !== 'Cancelled')
          .map((order, i) =>
            orderToOrderCardPropsMapper(
              order,
              DateTime.local(),
              findLocation(order),
              pastDueDirections[i],
              jobs[order.id],
            ),
          );

        setPastDueOrders(pastDueOrdersMapped.filter((mappedOrder) => mappedOrder.overDueAmount));
      } catch (e) {
        console.error(e);
        setLoadingError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (inspectionEnabled === undefined) {
      return;
    }

    if (from || to) {
      getOrders();
    }
  }, [from, to, location, searchText, allLocations, inspectionEnabled]);

  const filterOrders = (orders: OrderCardProcessedDetails[], includeOverdue: boolean = false) => {
    return orders.filter((order) => {
      if (hideActioned && !isActionable(order, includeOverdue)) return false;
      if (hideRequests && isRequest(order)) return false;
      return !(hideQuotes && isQuote(order));
    });
  };

  const filteredOrdersStartingToday = filterOrders(ordersStartingToday);
  const filteredOrdersEndingToday = filterOrders(ordersEndingToday);
  const filteredCombinedOrders = filterOrders(combinedOrders);
  const filteredPastDueOrders = filterOrders(pastDueOrders, true);

  return {
    loading,
    loadingError,
    summaryLoading,
    summaryError,
    summaryData,
    ordersStartingToday: filteredOrdersStartingToday,
    ordersEndingToday: filteredOrdersEndingToday,
    combinedOrders: filteredCombinedOrders,
    originalOrders,
    pastDueOrders: filteredPastDueOrders,
    unitTrackingViaUnitNum,
    drivers,
    allLocations,
    getOrders,
    location,
    setLocation,
    getSummary,
  };
};
